import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Clock, Star, ArrowLeft, Tag, Percent, Copy, Check, Ticket } from 'lucide-react';
import QRCode from 'react-qr-code';
import Button from '../components/Button';
import ReviewsSection from '../components/ReviewsSection';
import {
    useEstablishment,
    usePromotions,
    useLevels,
    useRedemptions,
    getPromoDiscountForLevel,
    getCustomPromotionPercentForSavings,
    type Promotion,
} from '../hooks/useData';
import { getImageUrl } from '../utils/getImageUrl';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { supabase } from '../lib/supabase';
import { useUserGeolocation } from '../hooks/useUserGeolocation';
import { distanceLabelFromCoords } from '../utils/distance';
import './Establishment.css';

interface RedemptionData {
    id: string;
    code: string;
    qrData: string;
    title: string;
    discount: string;
    establishmentName: string;
}

const Establishment = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const userFix = useUserGeolocation();
    const userPoint = userFix ? { lat: userFix.lat, lng: userFix.lng } : null;
    const { user, isAuthenticated } = useAuth();
    const { data: place, loading } = useEstablishment(id || '');
    const { data: promotions } = usePromotions(id || '', { publicOnly: true });
    const { data: levels } = useLevels();
    const { data: redemptions } = useRedemptions(user?.id);

    const totalVisits = user?.visitCount ?? redemptions?.filter(r => r.usedAt).length ?? 0;
    const sortedLevels = [...(levels || [])].sort((a, b) => a.minVisits - b.minVisits);
    const userLevelSlug = sortedLevels.filter((l) => totalVisits >= l.minVisits).pop()?.slug ?? 'bronze';
    const toast = useToast();
    const [showModal, setShowModal] = useState(false);
    const [redemptionData, setRedemptionData] = useState<RedemptionData | null>(null);
    const [showValidatedSuccess, setShowValidatedSuccess] = useState(false);
    const [redeeming, setRedeeming] = useState(false);
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState('');

    const [exclusiveCouponInput, setExclusiveCouponInput] = useState('');
    const [validatingExclusiveCode, setValidatingExclusiveCode] = useState(false);

    // Polling a cada 1s: verifica se o lojista validou o cupom (imperceptível para o usuário)
    const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        if (!showModal || !redemptionData?.id) return;

        const redemptionId = redemptionData.id;

        const checkUsed = async () => {
            try {
                const { data } = await supabase
                    .from('Redemption')
                    .select('usedAt')
                    .eq('id', redemptionId)
                    .single();
                if (data?.usedAt) {
                    if (pollIntervalRef.current) {
                        clearInterval(pollIntervalRef.current);
                        pollIntervalRef.current = null;
                    }
                    setShowModal(false);
                    setRedemptionData(null);
                    setCopied(false);
                    setShowValidatedSuccess(true);
                    toast.success('Cupom validado! Seu desconto foi aceito pelo estabelecimento.');
                }
            } catch {
                // falha silenciosa para não afetar a experiência
            }
        };

        pollIntervalRef.current = setInterval(checkUsed, 1000);
        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }
        };
    }, [showModal, redemptionData?.id, toast]);

    const activePromotions = promotions.filter(p => p.active && new Date(p.endDate) > new Date());

    if (loading) return <div className="p-4">Carregando...</div>;
    if (!place) return <div className="p-4">Local não encontrado</div>;

    const heroImageUrl = getImageUrl(place.image);

    const handleRedeemExclusiveCode = async () => {
        if (!id) return;
        const code = exclusiveCouponInput.trim();
        if (!code) {
            toast.warning('Digite o código do cupom.');
            return;
        }
        if (!isAuthenticated || !user) {
            setError('Faça login para resgatar cupons');
            return;
        }
        setValidatingExclusiveCode(true);
        setError('');
        try {
            const { data, error: rpcErr } = await supabase.rpc('validate_exclusive_promotion_code', {
                p_establishment_id: String(id),
                p_code: code,
            });
            if (rpcErr) throw new Error(rpcErr.message);
            const rows = Array.isArray(data) ? data : data != null ? [data] : [];
            const row = rows[0] as Promotion | undefined;
            if (!row?.id) {
                toast.error('Código inválido, expirado ou cupom esgotado.');
                return;
            }
            setExclusiveCouponInput('');
            void handleRedeemPromo({
                id: row.id,
                title: row.title,
                discount: getPromoDiscountForLevel(row, userLevelSlug),
                customDiscountPercent: getCustomPromotionPercentForSavings(row),
            });
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Não foi possível validar o código.';
            toast.error(msg);
            if (import.meta.env.DEV) console.error('[validate_exclusive_promotion_code]', e);
        } finally {
            setValidatingExclusiveCode(false);
        }
    };

    const handleRedeemPromo = async (promo: {
        id?: string;
        title: string;
        discount: string;
        customDiscountPercent?: number | null;
    }) => {
        if (!isAuthenticated || !user) {
            setError('Faça login para resgatar cupons');
            return;
        }
        setRedeeming(true);
        setError('');
        try {
            const payload = {
                p_user_id: String(user.id),
                p_establishment_id: String(id || ''),
                p_promotion_id: promo.id ? String(promo.id) : '',
                p_purchase_amount: 0,
                p_discount_saved: 0,
            };
            const { data, error: rpcError } = await supabase.rpc('create_redemption', payload);

            if (rpcError) throw new Error(rpcError.message || rpcError.details || 'Erro ao criar cupom');

            const raw = Array.isArray(data) ? data[0] : data;
            const row = (raw ?? {}) as Record<string, unknown>;
            const rid = String(row.id ?? '');
            const code = String(row.code ?? '').trim();
            const qrFromRpc = row.qrData ?? row.qr_data;
            const qrData =
                typeof qrFromRpc === 'string' && qrFromRpc.trim() !== ''
                    ? qrFromRpc.trim()
                    : `TURISTA:${String(id || '').trim()}:${code}`;

            setRedemptionData({
                id: rid,
                code,
                qrData,
                title: promo.title,
                discount: promo.discount,
                establishmentName: place.name,
            });
            setShowModal(true);
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Erro ao resgatar cupom';
            setError(msg);
            if (import.meta.env.DEV) console.error('[create_redemption]', err);
        } finally {
            setRedeeming(false);
        }
    };

    const copyCode = () => {
        if (redemptionData?.code) {
            navigator.clipboard.writeText(redemptionData.code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setRedemptionData(null);
        setCopied(false);
    };

    const closeValidatedSuccess = () => setShowValidatedSuccess(false);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    };

    return (
        <div className="establishment-page">
            <div className="hero-image-container">
                <img src={heroImageUrl} alt={place.name} className="hero-image" />
                <button className="back-btn" onClick={() => navigate(-1)} aria-label="Voltar">
                    <ArrowLeft size={24} />
                </button>
                <div className="hero-overlay"></div>
            </div>

            <div className="details-container">
                <div className="place-header">
                    <h1>{place.name}</h1>
                    <div className="rating-badge">
                        <Star size={14} fill="#FFD700" color="#FFD700" />
                        <span>{place.rating}</span>
                    </div>
                </div>

                <div className="info-row">
                    <MapPin size={18} className="info-icon" />
                    <span>
                        {place.address}
                        {' • '}
                        {distanceLabelFromCoords(userPoint, place.latitude, place.longitude, place.distance)}
                    </span>
                </div>

                <div className="info-row">
                    <Clock size={18} className="info-icon" />
                    <span>{place.openHours || 'Horário não informado'}</span>
                </div>

                {error && <div className="error-banner">{error}</div>}

                <div className="exclusive-coupon-card">
                    <h3 className="exclusive-coupon-title">
                        <Ticket size={18} aria-hidden />
                        Resgatar código do cupom
                    </h3>
                    <p className="exclusive-coupon-desc">
                        Cupons de parcerias (ex.: com influenciadores) não aparecem na lista abaixo. Digite o código que você recebeu para resgatar.
                    </p>
                    <div className="exclusive-coupon-row">
                        <input
                            type="text"
                            className="exclusive-coupon-input"
                            value={exclusiveCouponInput}
                            onChange={(e) => setExclusiveCouponInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && void handleRedeemExclusiveCode()}
                            placeholder="Digite o código do cupom"
                            maxLength={64}
                            autoComplete="off"
                            aria-label="Código do cupom exclusivo"
                        />
                        <Button
                            type="button"
                            variant="primary"
                            onClick={() => void handleRedeemExclusiveCode()}
                            disabled={validatingExclusiveCode || redeeming}
                        >
                            {validatingExclusiveCode ? '...' : 'Resgatar'}
                        </Button>
                    </div>
                </div>

                {activePromotions.length > 0 && (
                    <div className="promotions-section">
                        <h3><Tag size={18} /> Promoções Ativas</h3>
                        <div className="promotions-list-user">
                            {activePromotions.map(promo => (
                                <div key={promo.id} className="promo-card-user">
                                    <div className="promo-info-user">
                                        <span className="promo-discount-badge"><Percent size={14} />{getPromoDiscountForLevel(promo, userLevelSlug)}</span>
                                        <h4>{promo.title}</h4>
                                        <p className="promo-validity">Válido até {formatDate(promo.endDate)}</p>
                                        <p className="promo-uses-left">{promo.maxUses - promo.currentUses} cupons restantes</p>
                                    </div>
                                    <Button variant="primary"
                                        onClick={() => void handleRedeemPromo({
                                            id: promo.id,
                                            title: promo.title,
                                            discount: getPromoDiscountForLevel(promo, userLevelSlug),
                                            customDiscountPercent: getCustomPromotionPercentForSavings(promo),
                                        })}
                                        disabled={redeeming || promo.currentUses >= promo.maxUses}>
                                        {redeeming ? '...' : 'Resgatar'}
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {activePromotions.length === 0 && place.discount && (
                    <div className="benefit-card">
                        <div className="benefit-content">
                            <h3>Benefício Exclusivo</h3>
                            <p className="benefit-highlight">{place.discount}</p>
                            <p className="benefit-req">Consumindo acima de R$ 50,00</p>
                        </div>
                        <Button onClick={() => void handleRedeemPromo({ title: 'Benefício Exclusivo', discount: place.discount || '' })} disabled={redeeming}>
                            {redeeming ? '...' : 'Resgatar'}
                        </Button>
                    </div>
                )}

                <div className="description-section">
                    <h3>Sobre</h3>
                    {place.description?.trim() ? (
                        <div className="establishment-description-body preserve-text-layout">
                            {place.description}
                        </div>
                    ) : (
                        <p className="establishment-description-empty">Este estabelecimento ainda não adicionou uma descrição.</p>
                    )}
                </div>

                {((place.gallery && place.gallery.length > 0) || place.video) && (
                    <div className="media-section">
                        <h3>Mídia</h3>
                        {place.video && (
                            <div className="video-container" style={{ marginBottom: '1.5rem', borderRadius: '12px', overflow: 'hidden' }}>
                                <video src={getImageUrl(place.video)} controls style={{ width: '100%', maxHeight: '400px', backgroundColor: '#000' }} />
                            </div>
                        )}
                        {place.gallery && place.gallery.length > 0 && (
                            <div className="gallery-scroll">
                                {place.gallery.map((img, index) => (
                                    <img key={index} src={getImageUrl(img)} alt={`${place.name} - Foto ${index + 1}`}
                                        onClick={() => window.open(getImageUrl(img), '_blank')} style={{ cursor: 'pointer' }} />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <ReviewsSection establishmentId={id || ''} isAuthenticated={isAuthenticated} />

                {showModal && redemptionData && (
                    <div className="modal-overlay" onClick={closeModal}>
                        <div className="modal-content redemption-modal" onClick={e => e.stopPropagation()}>
                            <h3>🎉 Cupom Resgatado!</h3>
                            <p className="promo-modal-distance">
                                <MapPin size={14} aria-hidden />
                                {distanceLabelFromCoords(userPoint, place.latitude, place.longitude, place.distance)}
                            </p>
                            <p className="promo-modal-title">{redemptionData.title}</p>
                            <p className="promo-modal-discount">{redemptionData.discount}</p>
                            <div className="qr-container">
                                <QRCode value={redemptionData.qrData} size={150} style={{ height: 'auto', maxWidth: '100%', width: '100%' }} />
                            </div>
                            <div className="code-container">
                                <p className="code-label">Código alternativo:</p>
                                <div className="code-box" onClick={copyCode}>
                                    <span className="code-text">{redemptionData.code}</span>
                                    {copied ? <Check size={18} color="#22c55e" /> : <Copy size={18} />}
                                </div>
                                <p className="code-hint">Clique para copiar</p>
                            </div>
                            <p className="modal-instruction">Mostre este QR Code ou informe o código ao lojista. O valor da compra será informado por ele na hora da validação.</p>
                            <Button variant="secondary" onClick={closeModal} fullWidth>Fechar</Button>
                        </div>
                    </div>
                )}

                {showValidatedSuccess && (
                    <div className="modal-overlay" onClick={closeValidatedSuccess}>
                        <div className="modal-content redemption-modal validated-success" onClick={e => e.stopPropagation()}>
                            <h3>✅ Promoção Aceita!</h3>
                            <p className="promo-modal-title">Seu desconto foi validado pelo estabelecimento.</p>
                            <p style={{ color: '#64748b', fontSize: '0.95rem', marginBottom: '1rem' }}>Obrigado por utilizar o Facilita Pass+!</p>
                            <Button variant="primary" onClick={closeValidatedSuccess} fullWidth>OK</Button>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default Establishment;
